import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, ArrowRight, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const planId = searchParams.get('plan');

  const [status, setStatus] = useState('checking'); // checking | success | failed | pending
  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 8;

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return; }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async (attempt = 0) => {
    if (attempt >= MAX_ATTEMPTS) { setStatus('pending'); return; }
    try {
      const res = await fetch(`${API}/payments/status/${sessionId}`, { credentials: 'include' });
      const data = await res.json();
      setAttempts(attempt + 1);

      if (data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        return;
      }
      if (data.status === 'expired') { setStatus('failed'); return; }
      // Still processing — retry
      setTimeout(() => pollStatus(attempt + 1), 2500);
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 3000);
    }
  };

  const PLAN_NAMES = {
    weekly: 'Weekly Deal Boost — $29',
    monthly: 'Monthly Growth — $79',
    biweekly: '2-Week Campaign — $39',
  };

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        {status === 'checking' && (
          <div>
            <div className="w-16 h-16 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Processing Payment
            </h2>
            <p className="text-[#2B2B2B]/60 text-sm">Please wait while we confirm your payment...</p>
            {attempts > 0 && (
              <p className="text-xs text-[#8A9A8B] mt-3">Check {attempts}/{MAX_ATTEMPTS}...</p>
            )}
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Successful!
            </h2>
            <p className="text-[#2B2B2B]/65 mb-2">Your subscription is now active.</p>
            {planId && <p className="text-sm font-semibold text-[#C8A97E] mb-8">{PLAN_NAMES[planId] || planId}</p>}
            <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/15 mb-8 text-left space-y-3">
              {[
                'Your deals are now live and visible to students',
                'You can add/manage deals from your dashboard',
                'Deals auto-deactivate when your plan ends',
                'Renew anytime to keep your listing active',
              ].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-[#2B2B2B]/70">
                  <CheckCircle size={14} className="text-[#C8A97E] flex-shrink-0" />
                  {t}
                </div>
              ))}
            </div>
            <Link to="/business/dashboard" className="btn-primary inline-flex text-base px-8 py-3.5" data-testid="go-to-dashboard-btn">
              Go to My Dashboard <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {status === 'failed' && (
          <div data-testid="payment-failed">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-red-500" />
            </div>
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Payment Failed</h2>
            <p className="text-[#2B2B2B]/60 mb-8">Your payment was not completed. No charges were made.</p>
            <div className="flex gap-3 justify-center">
              <Link to="/pricing" className="btn-primary inline-flex">Try Again <ArrowRight size={14} /></Link>
              <Link to="/business/dashboard" className="btn-secondary inline-flex">Dashboard</Link>
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div data-testid="payment-pending">
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <Clock size={40} className="text-yellow-500" />
            </div>
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Payment Processing</h2>
            <p className="text-[#2B2B2B]/60 mb-6 text-sm">Your payment is being processed. This may take a moment. Your dashboard will update automatically once confirmed.</p>
            <button onClick={() => { setStatus('checking'); setAttempts(0); pollStatus(0); }} className="btn-primary inline-flex mb-3">
              <RefreshCw size={14} /> Check Again
            </button>
            <br />
            <Link to="/business/dashboard" className="text-sm text-[#8A9A8B] hover:text-[#C8A97E]">Go to Dashboard</Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
